/**
 * SaveButton — the heart toggle for a saveable (listing or estate).
 *
 * Holds NO save state of its own (SV-C1). The saved value and the save/unsave
 * mutation live in `useSaveState`, a shared TanStack Query overlay keyed by the
 * saveable. Every SaveButton for the same entity — the search card, the map
 * popup, the detail header — reads and writes the SAME cache slot, so their
 * hearts cannot diverge, and there is no prop-drilled `initialSaved`, no
 * per-button `useState`, and no effect syncing two sources.
 *
 * `serverSaved` is the authoritative server value the caller already holds (a
 * card's `saved`), read every render as the overlay's fallback — NOT a one-time
 * seed. With viewer-scoped search refetches (SV-A3) it stays correct across map
 * pans, so overlay-over-server is convergent.
 *
 * "use client": onClick, useTranslations, useAuthGate, and the shared-store
 * hooks all require the client.
 */

import { useSaveState } from '@/shared/hooks/use-saved-state';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { SaveableRef } from '@/shared/lib/saved/save-ref';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import { cn } from '@letsgethome/ui/lib/utils';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { Heart } from 'lucide-react';

interface SaveButtonProps {
  /** The saveable entity (listing or estate) to save/unsave. */
  saveable: SaveableRef;
  /** The authoritative server value; the shared overlay wins once the user acts. */
  serverSaved?: boolean;
  /** Size variant. */
  size?: 'sm' | 'md';
  /** Additional CSS classes. */
  className?: string;
}

export function SaveButton({
  saveable,
  serverSaved = false,
  size = 'sm',
  className,
}: SaveButtonProps) {
  const t = useTranslations('listings.card');
  const { requireAuth } = useAuthGate();
  const { saved, toggle, isPending } = useSaveState(saveable, serverSaved);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Signed out: gate, and replay this toggle after sign-in (SV-W3) instead of
    // dropping the click. Signed in: requireAuth returns true and we toggle now.
    if (!requireAuth(() => toggle())) return;
    if (isPending) return;
    toggle();
  };

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
  };

  return (
    <UnstyledButton
      type="button"
      onClick={handleClick}
      disabled={isPending}
      aria-label={saved ? t('saved') : t('save')}
      className={cn(
        'flex items-center justify-center rounded-full bg-background/90 backdrop-blur-sm transition-all hover:bg-background hover:scale-110',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        sizeClasses[size],
        className
      )}
    >
      <Heart
        className={cn(
          iconSizes[size],
          'transition-colors',
          saved
            ? 'fill-saved-heart stroke-saved-heart'
            : 'fill-transparent stroke-muted-foreground hover:stroke-foreground'
        )}
      />
    </UnstyledButton>
  );
}
